(() => {
  if (window.arroDynamicInitialized) return;
  window.arroDynamicInitialized = true;
  const mobile = window.matchMedia('(max-width: 700px)');
  const disclosureState = (root) => {
    root.querySelectorAll('[data-arro-dynamic] .responsive-details').forEach(detail => {
      detail.open = !mobile.matches || detail.dataset.collapseMobile === 'false';
    });
  };

  // Add to cart: add via AJAX and open the theme's cart drawer instead of
  // letting the native product form redirect to the cart page.
  const drawerTrigger = () => document.querySelector('a.js-drawer-open-cart');
  const cartDrawerEnabled = () => {
    const settings = window.theme && window.theme.settings;
    return !!(settings && settings.cartType === 'drawer' && drawerTrigger());
  };
  const openCartDrawer = (product) => {
    // custom.js exposes openCart(); fall back to the same steps it performs.
    if (typeof window.openCart === 'function') {
      window.openCart(product);
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:build'));
    const trigger = drawerTrigger();
    if (trigger) trigger.click();
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product } }));
  };
  const clearFormError = (form) => {
    form.querySelectorAll('.errors').forEach(error => error.remove());
  };
  const showFormError = (form, message) => {
    clearFormError(form);
    const error = document.createElement('p');
    error.className = 'errors';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    form.append(error);
  };
  const addToCart = (form) => {
    if (form.dataset.loading === 'true') return;
    const variant = form.querySelector('[name="id"]');
    const quantity = form.querySelector('[name="quantity"]');
    const button = form.querySelector('[data-arro-submit]');
    if (!variant || !variant.value) return;
    form.dataset.loading = 'true';
    clearFormError(form);
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    const release = () => {
      delete form.dataset.loading;
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };
    const routes = window.theme && window.theme.routes;
    const url = (routes && routes.cartAdd) || '/cart/add.js';
    const fallbackMessage = 'Unable to add this item to your cart. Please try again.';
    (window.theme?.cartReads?.request || fetch)(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variant.value,
        quantity: Math.max(1, parseInt(quantity && quantity.value, 10) || 1)
      })
    })
      .then(response => response.json().then(data => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          const description = data && data.description;
          showFormError(form, typeof description === 'string' ? description : (data && data.message) || fallbackMessage);
          return;
        }
        openCartDrawer(data);
      })
      .catch(error => {
        console.error(error);
        showFormError(form, fallbackMessage);
      })
      .finally(release);
  };

  // Guide: the call to action reveals the embedded Klaviyo form in a <dialog>,
  // either in place below the button (default) or as a modal popup (block
  // setting "Open the form in a popup"). Klaviyo renders into the container
  // inside it; a CSS-only "Loading" note covers the gap until it does (or the
  // fallback takes over).
  const guideIsPopup = (dialog) => dialog.dataset.arroGuideMode === 'popup';
  const syncGuideToggles = (dialog) => {
    document.querySelectorAll(`[data-arro-guide-toggle="${dialog.id}"][aria-expanded]`).forEach(toggle => {
      toggle.setAttribute('aria-expanded', dialog.open ? 'true' : 'false');
    });
  };
  const closeGuideDialog = (dialog) => {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    syncGuideToggles(dialog);
  };
  // Klaviyo only renders the embed when the form's targeting matches the
  // visitor (device, existing profile, custom JS trigger, ...). Ask it to
  // render on open; if nothing shows up in time, fall back to a built-in form
  // that subscribes to the same list through Klaviyo's client API.
  const GUIDE_RENDER_TIMEOUT = 3000;
  const guideContainer = (dialog) => dialog.querySelector('[class*="klaviyo-form-"]');
  const guideRendered = (dialog) => {
    const container = guideContainer(dialog);
    return !!(container && container.children.length);
  };
  const showGuideFallback = (dialog) => {
    const fallback = dialog.querySelector('[data-arro-guide-fallback]');
    if (!fallback || guideRendered(dialog)) return;
    dialog.dataset.fallback = 'true';
    fallback.hidden = false;
    const email = fallback.querySelector('[name="email"]');
    if (email && dialog.open) email.focus();
  };
  const requestGuideForm = (dialog) => {
    if (dialog.dataset.guideRequested || guideRendered(dialog)) return;
    dialog.dataset.guideRequested = 'true';
    const formId = dialog.dataset.arroGuideFormId;
    if (formId) {
      // Needed when the form is set to show only on a JavaScript trigger.
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    }
    const timer = setTimeout(() => showGuideFallback(dialog), GUIDE_RENDER_TIMEOUT);
    const container = guideContainer(dialog);
    if (!container || typeof MutationObserver !== 'function') return;
    const observer = new MutationObserver(() => {
      if (!guideRendered(dialog)) return;
      observer.disconnect();
      clearTimeout(timer);
    });
    observer.observe(container, { childList: true });
  };
  const subscribeGuideFallback = (form) => {
    if (form.dataset.loading === 'true') return;
    const email = form.querySelector('[name="email"]');
    const name = form.querySelector('[name="first_name"]');
    const button = form.querySelector('[data-arro-guide-submit]');
    const success = form.querySelector('.guide-dialog__fallback-success');
    const address = ((email && email.value) || '').trim();
    clearFormError(form);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      showFormError(form, 'Please enter a valid email address.');
      if (email) email.focus();
      return;
    }
    form.dataset.loading = 'true';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    const release = () => {
      delete form.dataset.loading;
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };
    const profile = {
      email: address,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    const firstName = ((name && name.value) || '').trim();
    if (firstName) profile.first_name = firstName;
    const companyId = form.dataset.arroCompanyId || window.__klKey || '';
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: '2025-07-15'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Arro guide sign-up',
            profile: { data: { type: 'profile', attributes: profile } }
          },
          relationships: { list: { data: { type: 'list', id: form.dataset.arroListId } } }
        }
      })
    })
      .then(response => {
        if (!response.ok) throw new Error('Klaviyo subscription failed: ' + response.status);
        form.querySelectorAll('input, [data-arro-guide-submit]').forEach(field => { field.hidden = true; });
        if (success) success.hidden = false;
      })
      .catch(error => {
        console.error(error);
        showFormError(form, 'Something went wrong. Please try again.');
      })
      .finally(release);
  };
  const openGuideDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    if (guideIsPopup(dialog) && typeof dialog.showModal === 'function') {
      dialog.showModal();
      document.documentElement.classList.add('arro-dialog-open');
    } else if (typeof dialog.show === 'function') {
      dialog.show();
    } else {
      // No <dialog> support: show the form in place.
      dialog.setAttribute('open', '');
    }
    syncGuideToggles(dialog);
    requestGuideForm(dialog);
  };
  const toggleGuideDialog = (dialog) => {
    if (!dialog) return;
    if (dialog.open) closeGuideDialog(dialog);
    else openGuideDialog(dialog);
  };
  const bindGuideDialog = (dialog) => {
    if (dialog.dataset.bound) return;
    dialog.dataset.bound = 'true';
    dialog.addEventListener('click', event => {
      // In popup mode a click on the backdrop targets the dialog element itself.
      const backdrop = event.target === dialog && guideIsPopup(dialog);
      if (backdrop || event.target.closest('[data-arro-guide-close]')) closeGuideDialog(dialog);
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('arro-dialog-open');
      syncGuideToggles(dialog);
    });
  };

  const initialize = (root = document) => {
    disclosureState(root);
    root.querySelectorAll('[data-arro-dynamic]').forEach(page => {
      if (page.dataset.initialized) return;
      page.dataset.initialized = 'true';
      page.addEventListener('change', event => {
        if (event.target.matches('[data-arro-variant]')) {
          const option = event.target.selectedOptions[0];
          if (!option) return;
          page.querySelectorAll('[data-arro-variant]').forEach(select => { select.value = option.value; });
          page.querySelectorAll('[data-arro-price]').forEach(price => { price.textContent = option.dataset.price; });
          page.querySelectorAll('[data-arro-submit]').forEach(button => {
            const available = option.dataset.available === 'true';
            button.disabled = !available;
            button.textContent = available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
          });
        }
        if (event.target.matches('[data-arro-quantity]') && event.target.validity.valid) {
          page.querySelectorAll('[data-arro-quantity]').forEach(input => { input.value = event.target.value; });
        }
      });
      page.querySelectorAll('[data-arro-guide-dialog]').forEach(bindGuideDialog);
      page.addEventListener('click', event => {
        const toggle = event.target.closest('[data-arro-guide-toggle]');
        if (toggle) toggleGuideDialog(document.getElementById(toggle.dataset.arroGuideToggle));
      });
      page.addEventListener('submit', event => {
        const fallback = event.target.closest('[data-arro-guide-fallback]');
        if (fallback) {
          event.preventDefault();
          subscribeGuideFallback(fallback);
          return;
        }
        const form = event.target.closest('[data-arro-form]');
        // Without a drawer (cart type "page", or no header cart icon) keep the native submit.
        if (!form || form.dataset.landingAddToCart === 'true' || !cartDrawerEnabled()) return;
        event.preventDefault();
        addToCart(form);
      });
    });
  };
  initialize();
  mobile.addEventListener('change', () => disclosureState(document));
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:block:select', event => {
    if (!event.target.closest('[data-arro-dynamic]')) return;
    event.target.querySelectorAll('.responsive-details').forEach(detail => { detail.open = true; });
  });
})();
